import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { getFileUrl } from '../utils/fileUtils';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    specs: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [techSheetFile, setTechSheetFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadProduct();
    }
  }, [id, isEdit]);

  const loadProduct = async () => {
    try {
      const product = await productsAPI.getOne(id);
      setFormData({
        name: product.name,
        description: product.description,
        specs: product.specs,
      });
      setImagePreview(getFileUrl(product.image));
      if (product.images && product.images.length > 0) {
        setExistingImages(product.images);
      }
    } catch (error) {
      toast.error('Error al cargar producto');
      navigate('/admin/dashboard');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImages([...additionalImages, ...files]);
      const previews = files.map(file => URL.createObjectURL(file));
      setAdditionalPreviews([...additionalPreviews, ...previews]);
    }
  };

  const removeAdditionalImage = (index) => {
    const newImages = additionalImages.filter((_, i) => i !== index);
    const newPreviews = additionalPreviews.filter((_, i) => i !== index);
    setAdditionalImages(newImages);
    setAdditionalPreviews(newPreviews);
  };

  const handleTechSheetChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTechSheetFile(file);
    }
  };

  const removeExistingImage = async (imageUrl) => {
    if (!window.confirm('¿Estás seguro de eliminar esta imagen?')) return;
    
    try {
      await productsAPI.removeImage(id, imageUrl);
      setExistingImages(existingImages.filter(img => img !== imageUrl));
      toast.success('Imagen eliminada');
    } catch (error) {
      toast.error('Error al eliminar imagen');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      data.append('specs', formData.specs);

      if (imageFile) {
        data.append('image', imageFile);
      } else if (!isEdit) {
        toast.error('La imagen principal es obligatoria');
        setLoading(false);
        return;
      }

      if (techSheetFile) {
        data.append('techSheet', techSheetFile);
      }

      let productId = id;
      
      if (isEdit) {
        await productsAPI.update(id, data);
        toast.success('Producto actualizado');
      } else {
        const newProduct = await productsAPI.create(data);
        productId = newProduct.id;
        toast.success('Producto creado');
      }

      // Add additional images if any
      if (additionalImages.length > 0 && productId) {
        await productsAPI.addImages(productId, additionalImages);
        toast.success(`${additionalImages.length} imágenes adicionales agregadas`);
      }

      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Error al guardar producto');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to="/admin/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Dashboard
          </Button>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {isEdit ? 'Editar Producto' : 'Nuevo Producto'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                  Nombre del Producto *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ej: Prensa Hidráulica Industrial"
                  required
                  className="border-stone-300"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-stone-700 mb-2">
                  Descripción *
                </label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descripción corta del producto"
                  required
                  className="border-stone-300"
                />
              </div>

              <div>
                <label htmlFor="specs" className="block text-sm font-medium text-stone-700 mb-2">
                  Especificaciones Técnicas *
                </label>
                <Textarea
                  id="specs"
                  name="specs"
                  value={formData.specs}
                  onChange={handleInputChange}
                  placeholder="Ej: Capacidad: 500 ton, Control digital"
                  required
                  rows={3}
                  className="border-stone-300 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Imagen Principal * {isEdit && '(opcional para editar)'}
                </label>
                <div className="space-y-4">
                  {imagePreview && (
                    <div className="relative w-full h-64 rounded-lg overflow-hidden border border-stone-300">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-center w-full">
                    <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-stone-700 rounded-lg border-2 border-stone-300 border-dashed cursor-pointer hover:bg-stone-50">
                      <Upload className="w-8 h-8 mb-2 text-stone-400" />
                      <span className="text-sm">
                        {imageFile ? imageFile.name : isEdit ? 'Click para cambiar imagen principal' : 'Click para subir imagen principal'}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Imágenes Adicionales (Opcional)
                </label>
                <p className="text-xs text-stone-500 mb-3">
                  Estas imágenes se mostrarán en un carrusel en la página del producto
                </p>
                
                {/* Existing images from database */}
                {existingImages.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-stone-600 mb-2">Imágenes existentes:</p>
                    <div className="grid grid-cols-3 gap-2">
                      {existingImages.map((img, index) => (
                        <div key={index} className="relative h-24 border border-stone-300 rounded group">
                          <img src={getFileUrl(img)} alt={`Existing ${index + 1}`} className="w-full h-full object-cover rounded" />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(img)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Eliminar imagen"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New images previews */}
                {additionalPreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {additionalPreviews.map((preview, index) => (
                      <div key={index} className="relative h-24 border border-stone-300 rounded">
                        <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => removeAdditionalImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-center w-full">
                  <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-stone-700 rounded-lg border-2 border-stone-300 border-dashed cursor-pointer hover:bg-stone-50">
                    <Upload className="w-8 h-8 mb-2 text-stone-400" />
                    <span className="text-sm">
                      Click para agregar más imágenes
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={handleAdditionalImagesChange}
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Ficha Técnica (PDF) - Opcional
                </label>
                <div className="flex items-center justify-center w-full">
                  <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-stone-700 rounded-lg border-2 border-stone-300 border-dashed cursor-pointer hover:bg-stone-50">
                    <Upload className="w-8 h-8 mb-2 text-stone-400" />
                    <span className="text-sm">
                      {techSheetFile ? techSheetFile.name : 'Click para subir PDF'}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf"
                      onChange={handleTechSheetChange}
                    />
                  </label>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-clay-primary hover:bg-clay-dark text-white py-6"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : isEdit ? 'Actualizar Producto' : 'Crear Producto'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/dashboard')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductForm;
